/**
 * ProfileModal
 * Modal för att visa och redigera den inloggade användarens profil
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BaseModal, ModalFooter } from '../common/BaseModal';
import { useAuth } from '@/store/authStore';
import { useAuthStore } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import { userService, UserProfile, UpdateProfileRequest, UserPreferences } from '@/services/user/userService';
import { bookingApi, PickupLocation } from '@/services/booking/bookingApi';
import {
  PencilIcon,
  UserCircleIcon,
  KeyIcon,
  IdentificationIcon,
  EyeIcon,
  EyeSlashIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Profile data from API
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');

  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCabinetPin, setShowCabinetPin] = useState(false);

  // Default pickup location (preference)
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [defaultPickupLocationId, setDefaultPickupLocationId] = useState<string>('');
  const [selectedPickupLocationId, setSelectedPickupLocationId] = useState<string>('');

  // Load profile when modal opens
  const loadProfile = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const data = await userService.getProfile();
      setProfile(data);
      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
      setEmail(data.email || '');
      setMobilePhone(data.MobilePhone || '');

      // Hämta utlämningsplatser för användarens organisation + sparad preferens parallellt.
      // Misslyckas dessa (t.ex. ingen booking-modul) döljs sektionen tyst.
      const [locations, prefs] = await Promise.all([
        bookingApi.getPickupLocations(false, true).catch(() => [] as PickupLocation[]),
        userService.getPreferences().catch(() => ({} as UserPreferences)),
      ]);
      setPickupLocations(locations);
      const savedId = prefs?.defaultPickupLocationId ? String(prefs.defaultPickupLocationId) : '';
      setDefaultPickupLocationId(savedId);
      setSelectedPickupLocationId(savedId);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [isOpen, t]);

  useEffect(() => {
    if (isOpen) {
      loadProfile();
      // Reset state when opening
      setIsEditing(false);
      setShowPasswordChange(false);
      setShowCabinetPin(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, loadProfile]);

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    // Validate password change
    if (showPasswordChange && newPassword) {
      if (!currentPassword) {
        setError(t('profile.currentPasswordRequired'));
        return;
      }
      if (newPassword.length < 6) {
        setError(t('profile.passwordTooShort'));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t('profile.passwordMismatch'));
        return;
      }
    }

    setSaving(true);
    try {
      const updateData: UpdateProfileRequest = {};

      if (firstName !== profile?.first_name) updateData.first_name = firstName;
      if (lastName !== profile?.last_name) updateData.last_name = lastName;
      if (email !== profile?.email) updateData.email = email;
      if (mobilePhone !== profile?.MobilePhone) updateData.MobilePhone = mobilePhone;

      if (showPasswordChange && newPassword) {
        updateData.current_password = currentPassword;
        updateData.new_password = newPassword;
      }

      const profileChanged = Object.keys(updateData).length > 0;
      const pickupChanged = selectedPickupLocationId !== defaultPickupLocationId;

      // Check if anything changed
      if (!profileChanged && !pickupChanged) {
        setIsEditing(false);
        return;
      }

      if (profileChanged) {
        const updatedProfile = await userService.updateProfile(updateData);

        // Update auth store so sidebar reflects changes
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            first_name: updatedProfile.first_name,
            last_name: updatedProfile.last_name,
            email: updatedProfile.email,
            MobilePhone: updatedProfile.MobilePhone,
          };
          // Update localStorage and store
          localStorage.setItem('user', JSON.stringify(updatedUser));
          useAuthStore.setState({ user: updatedUser });
        }

        // Update local profile state
        setProfile(prev => prev ? { ...prev, ...updatedProfile } : prev);
      }

      if (pickupChanged) {
        await userService.updatePreferences({
          defaultPickupLocationId: selectedPickupLocationId || null,
        });
        setDefaultPickupLocationId(selectedPickupLocationId);
      }

      setIsEditing(false);
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMessage(t('profile.saveSuccess'));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to profile values
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setEmail(profile.email || '');
      setMobilePhone(profile.MobilePhone || '');
    }
    setSelectedPickupLocationId(defaultPickupLocationId);
    setIsEditing(false);
    setShowPasswordChange(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const getRoleLabel = (role?: string | null) => {
    // Visa i första hand RBAC-rollens namn (roles.name) om det finns.
    if (profile?.role_name) return profile.role_name;
    if (!role) return '';
    const roleMap: Record<string, string> = {
      SUPER_ADMIN: 'Super Admin',
      ADMIN: 'Admin',
      FLEET_MANAGER: 'Fleet Manager',
      DRIVER: t('profile.roleDriver'),
      VIEWER: t('profile.roleViewer'),
    };
    return roleMap[role] || role;
  };

  const initials = `${(profile?.first_name || user?.first_name || '').charAt(0)}${(profile?.last_name || user?.last_name || '').charAt(0)}`.toUpperCase();

  return createPortal(
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('profile.title')}
      size="lg"
      loading={loading}
      footer={
        isEditing ? (
          <ModalFooter
            onCancel={handleCancel}
            onSubmit={handleSave}
            cancelText={t('common.cancel')}
            submitText={saving ? t('common.saving') : t('common.save')}
            loading={saving}
          />
        ) : undefined
      }
    >
      {/* Success message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {profile && (
        <div className="space-y-6">
          {/* Profile header with avatar */}
          <div className="flex items-center gap-4">
            <div className="rounded-full h-20 w-20 bg-BgPrimaryLight flex items-center text-bgPrimary justify-center text-2xl font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-textDefault">
                {profile.first_name} {profile.last_name}
              </h3>
              <p className="text-sm text-textSecondary">{profile.email}</p>
              <p className="text-xs text-textTertiary mt-1">
                {getRoleLabel(profile.role)} &middot; {profile.organization_name}
              </p>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-bgPrimary bg-BgPrimaryLight rounded-lg hover:bg-BgPrimaryLight/80 transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
                {t('profile.edit')}
              </button>
            )}
          </div>

          <div className="border-t border-border" />

          {/* Personal information */}
          <div>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-textDefault mb-3">
              <UserCircleIcon className="w-5 h-5" />
              {t('profile.personalInfo')}
            </h4>

            {isEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-textSecondary mb-1">
                    {t('profile.firstName')}
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-textSecondary mb-1">
                    {t('profile.lastName')}
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-textSecondary mb-1">
                    {t('profile.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-textSecondary mb-1">
                    {t('profile.phone')}
                  </label>
                  <input
                    type="tel"
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="+46..."
                  />
                </div>
                <CabinetPinField
                  pin={profile.ExternalUserPinCode}
                  revealed={showCabinetPin}
                  onToggle={() => setShowCabinetPin((v) => !v)}
                  t={t}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoField label={t('profile.firstName')} value={profile.first_name} />
                <InfoField label={t('profile.lastName')} value={profile.last_name} />
                <InfoField label={t('profile.email')} value={profile.email} />
                <InfoField label={t('profile.phone')} value={profile.MobilePhone || '-'} />
                <InfoField label={t('profile.role')} value={getRoleLabel(profile.role)} />
                <InfoField label={t('profile.organization')} value={profile.organization_name} />
                <CabinetPinField
                  pin={profile.ExternalUserPinCode}
                  revealed={showCabinetPin}
                  onToggle={() => setShowCabinetPin((v) => !v)}
                  t={t}
                />
              </div>
            )}
          </div>

          {/* Default pickup location */}
          {pickupLocations.length > 0 && (
            <>
              <div className="border-t border-border" />
              <div>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-textDefault mb-3">
                  <MapPinIcon className="w-5 h-5" />
                  {t('profile.defaultPickupLocation')}
                </h4>
                {isEditing ? (
                  <div>
                    <select
                      value={selectedPickupLocationId}
                      onChange={(e) => setSelectedPickupLocationId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                    <option value="">{t('profile.selectPickupLocation')}</option>
                    {pickupLocations.map((loc) => (
                      <option key={String(loc.id)} value={String(loc.id)}>
                        {loc.name}
                      </option>
                    ))}
                    </select>
                    <p className="mt-1 text-xs text-textTertiary">
                      {t('profile.defaultPickupLocationHint')}
                    </p>
                  </div>
                ) : (
                  <InfoField
                    label={t('profile.defaultPickupLocation')}
                    value={
                      pickupLocations.find((l) => String(l.id) === defaultPickupLocationId)?.name ||
                      t('profile.noDefaultPickupLocation')
                    }
                  />
                )}
              </div>
            </>
          )}

          {/* Password change */}
          {isEditing && profile.auth_method === 'PASSWORD' && (
            <>
              <div className="border-t border-border" />
              <div>
                <button
                  type="button"
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="flex items-center gap-2 text-sm font-semibold text-textDefault hover:text-bgPrimary transition-colors"
                >
                  <KeyIcon className="w-5 h-5" />
                  {t('profile.changePassword')}
                </button>

                {showPasswordChange && (
                  <div className="mt-3 space-y-3">
                    <div className="relative">
                      <label className="block text-xs font-medium text-textSecondary mb-1">
                        {t('profile.currentPassword')}
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-10 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-textTertiary hover:text-textDefault"
                        >
                          {showCurrentPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="relative">
                      <label className="block text-xs font-medium text-textSecondary mb-1">
                        {t('profile.newPassword')}
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-10 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder={t('profile.passwordMinLength')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-textTertiary hover:text-textDefault"
                        >
                          {showNewPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textSecondary mb-1">
                        {t('profile.confirmPassword')}
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Driver information */}
          {profile.driver && (
            <>
              <div className="border-t border-border" />
              <div>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-textDefault mb-3">
                  <IdentificationIcon className="w-5 h-5" />
                  {t('profile.driverInfo')}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {profile.driver.license_number && (
                    <InfoField label={t('profile.licenseNumber')} value={profile.driver.license_number} />
                  )}
                  {profile.driver.license_expiry && (
                    <InfoField
                      label={t('profile.licenseExpiry')}
                      value={new Date(profile.driver.license_expiry).toLocaleDateString('sv-SE')}
                    />
                  )}
                  {profile.driver.license_categories && (
                    <InfoField label={t('profile.licenseCategories')} value={profile.driver.license_categories} />
                  )}
                  {profile.driver.employee_id && (
                    <InfoField label={t('profile.employeeId')} value={profile.driver.employee_id} />
                  )}
                  {profile.driver.department && (
                    <InfoField label={t('profile.department')} value={profile.driver.department} />
                  )}
                  {profile.driver.cost_center && (
                    <InfoField label={t('profile.costCenter')} value={profile.driver.cost_center} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </BaseModal>,
    document.body
  );
};

/** Read-only info field */
const InfoField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="block text-xs font-medium text-textTertiary">{label}</span>
    <span className="text-sm text-textDefault">{value}</span>
  </div>
);

const CabinetPinField: React.FC<{
  pin?: string | null;
  revealed: boolean;
  onToggle: () => void;
  t: (key: string) => string;
}> = ({ pin, revealed, onToggle, t }) => {
  const value = pin?.trim() || '';
  return (
    <div>
      <span className="block text-xs font-medium text-textTertiary">{t('profile.cabinetPin')}</span>
      {value ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-textDefault font-mono tracking-widest">
            {revealed ? value : '••••••••'.slice(0, Math.max(4, value.length))}
          </span>
          <button
            type="button"
            onClick={onToggle}
            className="p-1 rounded hover:bg-bgElevated text-textSecondary"
            aria-label={t('profile.cabinetPin')}
          >
            {revealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
        </div>
      ) : (
        <span className="text-sm text-textDefault">{t('profile.noCabinetPin')}</span>
      )}
      <span className="block text-xs text-textTertiary mt-0.5">{t('profile.cabinetPinHint')}</span>
    </div>
  );
};

export default ProfileModal;
